// src/components/dashboard/GuidedPath.jsx
//
// The Guided Path drawn as a CLIMB: a winding trail from base camp (bottom
// left, where the weekly pulse check-in sits) to the summit (the last content
// step, top right) over a soft mountain backdrop in the org's brand colour. Checkpoints sit on the trail;
// the trail fills in behind "you are here". Tapping a checkpoint shows its
// plan in the side card, which is where Start / Mark done live.
//
// Node order comes from src/utils/guidedPath.js (shared with the assessment
// completion screen, so both surfaces point at the same next step). Every
// checkpoint is tappable — nothing is hard-locked.
//
// Persistence: completion lives on users/{uid}.pathProgress as a map of
// { [nodeId]: true } (in-session for super-admin preview). The app streams the
// user doc, so completion arrives via the currentUser prop. Steps complete via
// "Mark done" here or automatically when a video finishes in the topic the
// checkpoint sent the user to (see TopicView).

import React, { useMemo, useState, useLayoutEffect, useRef, useEffect } from 'react';
import {
    Check, ChevronRight, Zap, Moon, Target, Shield, Flame, Award,
    Eye, Repeat, ListChecks, Apple, HeartPulse, HeartHandshake, Compass, Flag, Mountain
} from 'lucide-react';
import {
    buildPathNodes, normalizePathProgress, isPathNodeComplete, pickRecommendedNode,
    setActivePathNode, markPathNodeDone
} from '../../utils/guidedPath';
import { getActionPlanForNode } from '../../data/actionPlans';
import { worksheetsForTopic } from '../../data/worksheets';
import { setPendingWorksheet } from '../../utils/guidedPath';
import { useOrganization } from '../../context/OrganizationContext';
import { resolvePathCharacter } from '../../data/pathCharacters';
import { PATH_CHARACTER_ART, DogSled } from './PathCharacters';

// node.iconKey -> lucide icon (recovery keys + metric ids + milestone)
const NODE_ICONS = {
    HeartHandshake, Moon,
    focus: Target,
    resilience: Shield,
    motivation: Flame,
    confidence: Award,
    discipline: ListChecks,
    awareness: Eye,
    adaptability: Repeat,
    sleep_perf: Moon,
    nutrition: Apple,
    injury_mgmt: HeartPulse,
    pulse: Zap
};

// Trail geometry in viewBox units. The SVG stretches to its container
// (preserveAspectRatio="none") so the climb fits any card width; strokes use
// vector-effect so they stay crisp. Checkpoints are HTML buttons positioned at
// percentages derived from points along this path.
const VB_W = 400;
const VB_H = 480;
const TRAIL_D = 'M 48 436 C 200 442 340 424 330 372 C 322 330 90 332 84 284 C 78 236 320 238 322 190 C 324 146 110 152 118 106 C 124 72 262 60 350 58';
const SUMMIT = { x: 350, y: 58 };

// Short label for the trail (the side card carries the full title)
const shortTitle = (node) => {
    if (node.milestone) return 'Weekly Pulse';
    if (node.label) return node.label;
    return node.title;
};

// The traveller's artwork lives in ./PathCharacters (dog sled, hockey player…);
// which one an org gets is organizations/{id}.pathCharacter.

// The sled never sits exactly on the first/last pin
const MUSHER_MIN_T = 0.09;
const MUSHER_MAX_T = 0.96;
const MUSHER_MS = 900;

const GuidedPath = ({ scores, currentUser, isPulseDue, onNavigate }) => {
    // Which traveller this org's members see. Always resolves to real artwork.
    const { orgData } = useOrganization();
    const Traveller = PATH_CHARACTER_ART[resolvePathCharacter(orgData)] || DogSled;
    const [savingId, setSavingId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [points, setPoints] = useState([]);
    const [trailLength, setTrailLength] = useState(0);
    const trailRef = useRef(null);
    const fillRef = useRef(null);     // travelled line (sub-path rebuilt each frame)
    const musherRef = useRef(null);   // positioned wrapper (left/top set imperatively)
    const sledRef = useRef(null);     // inner wrapper, mirrored when heading left
    const shownT = useRef(null);      // where the sled currently is (fraction of trail)

    const hasData = !!scores?.hasData;
    const completedMap = useMemo(() => normalizePathProgress(currentUser?.pathProgress), [currentUser?.pathProgress]);
    const nodes = useMemo(() => buildPathNodes({ scores, isPulseDue }), [scores, isPulseDue]);

    const ctx = { hasData, isPulseDue };
    const isNodeComplete = (node) => isPathNodeComplete(node, completedMap, ctx);
    const completedCount = nodes.filter(isNodeComplete).length;
    const recommended = pickRecommendedNode(nodes, completedMap, ctx);
    const recommendedId = recommended?.id;
    const allDone = nodes.length > 0 && !recommended;

    // Selected checkpoint (side card). Defaults to "you are here".
    const selected = nodes.find(n => n.id === selectedId) || recommended || nodes[nodes.length - 1] || null;
    const selectedPlan = getActionPlanForNode(selected);

    // Reset a stale selection when the node set changes (new assessment)
    useEffect(() => {
        if (selectedId && !nodes.some(n => n.id === selectedId)) setSelectedId(null);
    }, [nodes, selectedId]);

    // Place checkpoints evenly along the trail (user units are viewBox units
    // regardless of how the SVG is stretched).
    useLayoutEffect(() => {
        const el = trailRef.current;
        if (!el || nodes.length === 0) return;
        const total = el.getTotalLength();
        const n = nodes.length;
        const pts = nodes.map((_, i) => {
            const t = n === 1 ? 1 : i / (n - 1);
            const p = el.getPointAtLength(total * t);
            return { x: p.x, y: p.y, t };
        });
        setPoints(pts);
        setTrailLength(total);
    }, [nodes]);

    // Trail fills to just beyond the furthest done checkpoint: halfway to the
    // next one (nothing if nothing is done yet, the summit when all done). The
    // sled parks exactly where the filled line ends.
    const lastDoneIdx = nodes.reduce((max, node, idx) => (isNodeComplete(node) ? idx : max), -1);
    const fillT = allDone
        ? 1
        : lastDoneIdx < 0
            ? 0
            : ((points[lastDoneIdx]?.t ?? 0) + (points[lastDoneIdx + 1]?.t ?? 1)) / 2;
    const musherT = trailLength ? Math.min(MUSHER_MAX_T, Math.max(MUSHER_MIN_T, fillT)) : null;

    // Drive the sled ALONG the trail (sampling the curve each frame) rather than
    // in a straight line between two points; mirrored when heading left.
    useLayoutEffect(() => {
        const el = trailRef.current;
        const wrap = musherRef.current;
        if (!el || !wrap || !trailLength || musherT == null) return undefined;
        // The travelled line is an explicit sub-path sampled from the curve
        // (not a dash offset: with non-scaling-stroke, dashes are measured in
        // screen pixels, so a dash-based fill drifts away from the sled when
        // the box is not 1:1 with the viewBox). Its end IS the sled.
        const SAMPLES = 90;
        const place = (t) => {
            const pt = el.getPointAtLength(trailLength * t);
            const a = el.getPointAtLength(trailLength * Math.max(0, t - 0.01));
            const b = el.getPointAtLength(trailLength * Math.min(1, t + 0.01));
            wrap.style.left = ((pt.x / VB_W) * 100) + '%';
            wrap.style.top = ((pt.y / VB_H) * 100) + '%';
            if (sledRef.current) sledRef.current.style.transform = b.x < a.x ? 'scaleX(-1)' : 'none';
            if (fillRef.current) {
                const upto = Math.floor(SAMPLES * t);
                let d = '';
                for (let i = 0; i <= upto; i++) {
                    const q = el.getPointAtLength(trailLength * (i / SAMPLES));
                    d += (i === 0 ? 'M ' : ' L ') + q.x.toFixed(1) + ' ' + q.y.toFixed(1);
                }
                d += ' L ' + pt.x.toFixed(1) + ' ' + pt.y.toFixed(1);
                fillRef.current.setAttribute('d', d);
            }
        };
        const from = shownT.current == null ? musherT : shownT.current;
        if (from === musherT) { place(musherT); shownT.current = musherT; return undefined; }
        let start = null;
        let raf = 0;
        const step = (ts) => {
            if (start == null) start = ts;
            const k = Math.min(1, (ts - start) / MUSHER_MS);
            const eased = 1 - Math.pow(1 - k, 3);
            const t = from + (musherT - from) * eased;
            place(t);
            shownT.current = t;
            if (k < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [musherT, trailLength]);

    const openNode = (node) => {
        setActivePathNode(node);
        onNavigate(node.route);
    };

    // Land straight in the checkpoint's checklist (first sheet for the topic)
    const openNodeSheet = (node, sheet) => {
        setActivePathNode(node);
        setPendingWorksheet(sheet.id);
        onNavigate(node.route);
    };

    const handleToggleDone = async (node) => {
        if (!currentUser?.uid || node.milestone || savingId) return;
        setSavingId(node.id);
        try {
            await markPathNodeDone(currentUser.uid, node.id, !completedMap[node.id]);
        } catch (e) {
            console.error('Error saving path progress:', e);
        } finally {
            setSavingId(null);
        }
    };

    // --- NO BASELINE: invite instead of an empty trail ---
    if (!hasData) {
        return (
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col items-center text-center animate-in fade-in duration-500">
                <div className="w-14 h-14 bg-brand-light text-brand-primary rounded-2xl flex items-center justify-center mb-4">
                    <Mountain size={28} />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-1">Your Guided Path</h3>
                <p className="text-sm text-slate-500 max-w-md mb-5 font-medium">
                    Complete your baseline to unlock a personal, step-by-step climb built from your own results.
                </p>
                <button
                    onClick={() => onNavigate('assessment')}
                    className="px-6 py-3 bg-brand-primary text-white rounded-full font-bold hover:bg-brand-dark transition-all shadow-md flex items-center gap-2"
                >
                    Start Baseline <ChevronRight size={16} />
                </button>
            </div>
        );
    }

    const selectedDone = selected ? isNodeComplete(selected) : false;
    const selectedIsRecommended = selected?.id === recommendedId;
    const summitId = nodes[nodes.length - 1]?.id;

    return (
        <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm animate-in fade-in duration-500">
            {/* HEADER: title + streak + progress */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
                <div className="flex items-center gap-2">
                    <Compass size={20} className="text-brand-primary" />
                    <h3 className="text-lg font-black text-slate-800">Your Guided Path</h3>
                </div>
                <div className="flex items-center gap-3">
                    {currentUser?.pulseStreak > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs font-black text-orange-500 bg-orange-50 border border-orange-100 px-3 py-1 rounded-full">
                            🔥 {currentUser.pulseStreak} Week Streak
                        </span>
                    )}
                    <span className="text-xs font-black text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                        {completedCount} of {nodes.length} done
                    </span>
                </div>
            </div>

            {/* progress bar */}
            <div className="w-full h-1.5 bg-slate-100 rounded-full mb-6 overflow-hidden">
                <div
                    className="h-full bg-brand-primary rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${nodes.length > 0 ? (completedCount / nodes.length) * 100 : 0}%` }}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-6 items-stretch">
                {/* ===== THE CLIMB ===== */}
                <div className="flex flex-col gap-2">
                <div className="relative rounded-3xl overflow-hidden bg-gradient-to-b from-sky-50 via-white to-slate-50 border border-slate-100 aspect-[4/5] sm:aspect-[5/4] md:aspect-auto md:min-h-[440px]">
                    <svg
                        className="absolute inset-0 w-full h-full"
                        viewBox={`0 0 ${VB_W} ${VB_H}`}
                        preserveAspectRatio="none"
                        aria-hidden="true"
                    >
                        {/* mountain backdrop — brand colour, very light */}
                        <polygon
                            points={`0,${VB_H} 70,330 130,380 210,250 270,320 330,200 ${VB_W},280 ${VB_W},${VB_H}`}
                            style={{ fill: 'var(--brand-primary)' }}
                            opacity="0.06"
                        />
                        <polygon
                            points={`0,${VB_H} 40,400 120,300 190,360 260,230 ${SUMMIT.x},${SUMMIT.y + 40} ${VB_W},130 ${VB_W},${VB_H}`}
                            style={{ fill: 'var(--brand-primary)' }}
                            opacity="0.10"
                        />
                        {/* snow cap */}
                        <polygon
                            points={`${SUMMIT.x - 34},${SUMMIT.y + 76} ${SUMMIT.x - 6},${SUMMIT.y + 44} ${SUMMIT.x + 16},${SUMMIT.y + 62} ${SUMMIT.x + 34},${SUMMIT.y + 76}`}
                            fill="white"
                            opacity="0.8"
                        />

                        {/* trail ahead (dashed) */}
                        <path
                            ref={trailRef}
                            d={TRAIL_D}
                            fill="none"
                            stroke="#cbd5e1"
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeDasharray="8 10"
                            vectorEffect="non-scaling-stroke"
                        />
                        {/* trail travelled — its "d" is rebuilt each frame up to the sled */}
                        <path
                            ref={fillRef}
                            d=""
                            fill="none"
                            style={{ stroke: 'var(--brand-primary)' }}
                            strokeWidth="5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            vectorEffect="non-scaling-stroke"
                        />
                    </svg>

                    <span className="absolute right-3 top-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400 z-20">Summit</span>

                    {/* the climber — driven along the trail by the effect above */}
                    {musherT != null && (
                        <div
                            ref={musherRef}
                            className="absolute w-[84px] md:w-[104px] pointer-events-none z-10"
                            style={{ transform: 'translate(-50%, -92%)' }}
                            aria-hidden="true"
                        >
                            <div className="musher-bob">
                                <div ref={sledRef}>
                                    <Traveller />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* checkpoints */}
                    {points.length === nodes.length && nodes.map((node, idx) => {
                        const p = points[idx];
                        const done = isNodeComplete(node);
                        const isHere = node.id === recommendedId;
                        const isSelected = selected?.id === node.id;
                        const Icon = NODE_ICONS[node.iconKey] || Zap;
                        const isSummit = node.id === summitId;
                        const labelRight = p.x < VB_W * 0.55;
                        // caption placement for "you are here": keep it inside the box
                        const capY = p.y < VB_H * 0.22 ? 'top-full mt-2' : 'bottom-full mb-2';
                        const capX = p.x < VB_W * 0.3 ? 'left-0' : p.x > VB_W * 0.7 ? 'right-0' : 'left-1/2 -translate-x-1/2';
                        return (
                            <button
                                key={node.id}
                                type="button"
                                onClick={() => setSelectedId(node.id)}
                                aria-label={`${node.title}${done ? ' (done)' : isHere ? ' (you are here)' : ''}`}
                                aria-pressed={isSelected}
                                className="absolute -translate-x-1/2 -translate-y-1/2 group focus:outline-none"
                                style={{ left: `${(p.x / VB_W) * 100}%`, top: `${(p.y / VB_H) * 100}%` }}
                            >
                                <div className="relative flex items-center">
                                    {/* pulse ring for "you are here" */}
                                    {isHere && !done && (
                                        <span className="absolute inset-0 rounded-full bg-brand-primary/30 animate-ping" aria-hidden="true" />
                                    )}
                                    <div
                                        className={`relative w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center border-[3px] shadow-md transition-all ${
                                            done
                                                ? 'bg-emerald-500 border-white text-white'
                                                : isHere
                                                    ? 'bg-brand-primary border-white text-white'
                                                    : isSummit
                                                        ? 'bg-amber-400 border-white text-white'
                                                        : 'bg-white border-slate-200 text-slate-400'
                                        } ${isSelected ? 'ring-4 ring-brand-primary/25 scale-110' : 'group-hover:scale-105'}`}
                                    >
                                        {done ? <Check size={20} strokeWidth={3} /> : <Icon size={18} />}
                                    </div>
                                    {isSummit && (
                                        <span className="absolute -top-2 -right-1 w-5 h-5 rounded-full bg-white border border-amber-300 text-amber-500 flex items-center justify-center shadow-sm" aria-hidden="true">
                                            <Flag size={10} strokeWidth={3} />
                                        </span>
                                    )}

                                    {/* label */}
                                    <span
                                        className={`absolute whitespace-nowrap text-[10px] md:text-[11px] font-black px-2 py-0.5 rounded-full border shadow-sm ${
                                            isHere && !done
                                                ? capY + ' ' + capX
                                                : labelRight ? 'top-1/2 -translate-y-1/2 left-full ml-2' : 'top-1/2 -translate-y-1/2 right-full mr-2'
                                        } ${
                                            isHere && !done
                                                ? 'bg-brand-primary text-white border-brand-primary'
                                                : done
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                                    : 'bg-white/95 text-slate-600 border-slate-200'
                                        }`}
                                    >
                                        {isHere && !done ? 'You are here · ' : ''}{shortTitle(node)}
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>
                <div className="px-1 text-[9px] font-black uppercase tracking-widest text-slate-400">
                    <span>Base camp · weekly pulse</span>
                </div>
                </div>

                {/* ===== CHECKPOINT CARD ===== */}
                {selected && (
                    <div className={`rounded-3xl border p-5 md:p-6 flex flex-col ${
                        selectedIsRecommended ? 'bg-brand-light border-brand-primary/40' : selectedDone ? 'bg-emerald-50/60 border-emerald-100' : 'bg-slate-50 border-slate-200'
                    }`}>
                        <div className="flex items-center gap-2 mb-2">
                            {selectedIsRecommended && !selectedDone && (
                                <span className="inline-block text-[9px] font-black text-brand-primary uppercase tracking-widest bg-white px-2 py-0.5 rounded-full border border-brand-primary/20">
                                    Recommended next
                                </span>
                            )}
                            {selectedDone && (
                                <span className="inline-flex items-center gap-1 text-[9px] font-black text-emerald-700 uppercase tracking-widest bg-white px-2 py-0.5 rounded-full border border-emerald-200">
                                    <Check size={10} strokeWidth={3} /> Done
                                </span>
                            )}
                            {selected.milestone && (
                                <span className="inline-block text-[9px] font-black text-slate-500 uppercase tracking-widest bg-white px-2 py-0.5 rounded-full border border-slate-200">
                                    Base camp · weekly check-in
                                </span>
                            )}
                            {selected.id === summitId && !selected.milestone && (
                                <span className="inline-block text-[9px] font-black text-amber-700 uppercase tracking-widest bg-white px-2 py-0.5 rounded-full border border-amber-200">
                                    Summit
                                </span>
                            )}
                        </div>
                        <h4 className="text-lg font-black text-slate-900 leading-tight">{selected.title}</h4>
                        <p className="text-sm text-slate-600 font-medium leading-relaxed mt-1">{selectedPlan?.context || selected.desc}</p>

                        {selectedPlan?.steps?.length > 0 && (
                            <ol className="mt-4 space-y-2.5">
                                {selectedPlan.steps.map((step, i) => (
                                    <li key={i} className="flex items-start gap-3 bg-white/80 border border-white rounded-xl p-2.5">
                                        <span className="w-6 h-6 rounded-full bg-brand-light text-brand-primary flex items-center justify-center text-[11px] font-black shrink-0">{i + 1}</span>
                                        <div>
                                            <p className="text-xs font-black text-slate-800 leading-tight">{step.title}</p>
                                            <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-0.5">{step.desc}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        )}

                        {!selected.milestone && worksheetsForTopic(selected.route).length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                                {worksheetsForTopic(selected.route).map(sheet => (
                                    <button key={sheet.id} onClick={() => openNodeSheet(selected, sheet)}
                                        className="px-3 py-2 rounded-lg text-xs font-bold border border-brand-primary/30 bg-white text-brand-primary hover:bg-brand-light transition-colors">
                                        {sheet.kind === 'checklist' ? 'Open checklist' : 'Open worksheet'}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="mt-auto pt-5 flex flex-col sm:flex-row gap-2">
                            <button
                                onClick={() => openNode(selected)}
                                className="flex-1 py-3 bg-brand-primary hover:bg-brand-dark text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors shadow-md"
                            >
                                {selected.milestone
                                    ? (isPulseDue ? 'Take weekly pulse' : 'Update assessment')
                                    : (selectedPlan?.actionText || 'Start')}
                                <ChevronRight size={16} />
                            </button>
                            {!selected.milestone && (
                                <button
                                    onClick={() => handleToggleDone(selected)}
                                    disabled={savingId === selected.id}
                                    className={`py-3 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 border transition-colors ${
                                        selectedDone
                                            ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600'
                                            : 'bg-white border-slate-200 text-slate-600 hover:border-brand-primary/40 hover:text-brand-primary'
                                    } ${savingId === selected.id ? 'opacity-50' : ''}`}
                                >
                                    <Check size={14} strokeWidth={3} /> {selectedDone ? 'Done' : 'Mark done'}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {allDone && (
                <p className="text-center text-xs font-bold text-emerald-600 mt-4">Summit reached this week. Your next pulse starts a fresh climb.</p>
            )}
        </div>
    );
};

export default GuidedPath;
